import {
  COMPLIANCE_RULES,
  type ComplianceFinding,
  type ComplianceSeverity,
  complianceRule,
  complianceSeverity,
} from '@facture/contracts'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  AlertTriangleIcon,
  CalendarSearchIcon,
  CheckCircle2Icon,
  HardHatIcon,
  InfoIcon,
  OctagonAlertIcon,
  RefreshCwIcon,
  ScaleIcon,
  ShieldCheckIcon,
} from 'lucide-react'
import { useMemo } from 'react'
import { z } from 'zod'
import { AnimatedNumber, Stagger, StaggerItem } from '@/components/motion'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { columnHelper, DataTable } from '@/components/ui/data-table'
import { ClearFilters, FilterBar, FilterDateRange, FilterSelect } from '@/components/ui/filter-bar'
import { Avatar, Tooltip } from '@/components/ui/misc'
import { EmptyState, Page, PageHeader } from '@/components/ui/page'
import { Skeleton } from '@/components/ui/skeleton'
import { complianceQuery } from '@/features/compliance/api'
import {
  CHECKS,
  findingValue,
  findingWhen,
  ruleLabel,
  SEVERITY,
  SeverityBadge,
  SeverityIcon,
} from '@/features/compliance/compliance-ui'
import { projectOptionsQuery } from '@/features/projects/api'
import { addDaysISO, todayCR } from '@/lib/dates'
import { cn } from '@/lib/utils'

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()

const searchSchema = z.object({
  project: z.string().optional(),
  worker: z.string().optional(),
  severity: complianceSeverity.optional(),
  rule: complianceRule.optional(),
  from: isoDate,
  to: isoDate,
})

export const Route = createFileRoute('/_app/projects/compliance')({
  staticData: { crumb: 'Cumplimiento' },
  validateSearch: searchSchema,
  component: CompliancePage,
})

const col = columnHelper<ComplianceFinding>()

/** Tablero de cumplimiento legal: los auditores del Código de Trabajo sobre las jornadas de todas las obras. */
function CompliancePage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const queryClient = useQueryClient()
  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  const today = todayCR()
  const to = search.to ?? today
  const from = search.from ?? addDaysISO(to, -29)
  const report = useQuery(
    complianceQuery({
      from,
      to,
      projectId: search.project,
      workerId: search.worker,
      severity: search.severity,
      rule: search.rule,
    }),
  )
  const projects = useQuery(projectOptionsQuery)
  const data = report.data
  const counts = data?.counts

  const openDay = (f: ComplianceFinding) =>
    navigate({
      to: '/projects/$id',
      params: { id: f.projectId },
      search: { tab: 'personal', week: f.date },
    })

  const columns = useMemo(
    () => [
      col.accessor('title', {
        header: 'Hallazgo',
        cell: ({ row }) => {
          const f = row.original
          return (
            <div className="flex min-w-0 items-start gap-3 py-0.5">
              <SeverityIcon severity={f.severity} className="mt-0.5" />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-medium">{f.title}</span>
                  {f.fixableByRecompute && (
                    <Tooltip content="El día quedó con un pago que el motor actual corrige: recalculalo desde la pestaña Personal del proyecto.">
                      <span className="rounded-md bg-primary-soft px-1.5 py-0.5 font-medium text-[11px] text-primary-soft-foreground">
                        Se corrige recalculando
                      </span>
                    </Tooltip>
                  )}
                </div>
                <div className="mt-0.5 text-muted-foreground text-xs">
                  {ruleLabel(f.rule)}
                  {f.article ? ` · ${f.article}` : ' · sin norma expresa'}
                  <span className="md:hidden">
                    {' '}
                    · {f.workerName} · {findingWhen(f)}
                  </span>
                </div>
                <p className="mt-1 hidden max-w-2xl text-muted-foreground text-xs leading-relaxed lg:line-clamp-2">
                  {f.detail}
                </p>
              </div>
            </div>
          )
        },
      }),
      col.accessor('severity', {
        header: 'Nivel',
        meta: { hideOnMobile: true },
        cell: ({ getValue }) => <SeverityBadge severity={getValue()} />,
      }),
      col.accessor('workerName', {
        header: 'Trabajador',
        meta: { hideOnMobile: true },
        cell: ({ row }) => {
          const f = row.original
          return (
            <div className="flex min-w-0 items-center gap-2.5">
              <Avatar name={f.workerName} className="hidden size-8 xl:flex" />
              <div className="min-w-0">
                <div className="truncate text-sm">{f.workerName}</div>
                <div className="flex min-w-0 items-center gap-1 text-muted-foreground text-xs">
                  <HardHatIcon className="size-3 shrink-0" />
                  <span className="truncate">
                    {f.projectCode ?? f.projectName}
                    {f.otherProjects.length ? ` + ${f.otherProjects.length}` : ''}
                  </span>
                </div>
              </div>
            </div>
          )
        },
      }),
      col.accessor('date', {
        header: 'Fecha',
        meta: { hideOnMobile: true, className: 'whitespace-nowrap' },
        cell: ({ row }) => <span className="text-muted-foreground tabular">{findingWhen(row.original)}</span>,
      }),
      col.accessor('value', {
        header: 'Medida',
        meta: { align: 'right', hideOnMobile: true, className: 'whitespace-nowrap' },
        cell: ({ row }) => <span className="font-medium">{findingValue(row.original)}</span>,
      }),
      col.display({
        id: 'open',
        header: '',
        meta: { align: 'right', className: 'w-0' },
        cell: ({ row }) => (
          <Tooltip content="Abrir la semana en la hoja de Personal">
            <Button
              asChild
              variant="ghost"
              size="icon-sm"
              onClick={(e) => e.stopPropagation()}
              aria-label={`Ver el ${findingWhen(row.original)} de ${row.original.workerName}`}
            >
              <Link
                to="/projects/$id"
                params={{ id: row.original.projectId }}
                search={{ tab: 'personal', week: row.original.date }}
              >
                <CalendarSearchIcon />
              </Link>
            </Button>
          </Tooltip>
        ),
      }),
    ],
    [],
  )

  const filtered = !!(
    search.project ||
    search.worker ||
    search.severity ||
    search.rule ||
    search.from ||
    search.to
  )
  const severityFilter = (s: ComplianceSeverity) =>
    setSearch({ severity: search.severity === s ? undefined : s })

  return (
    <Page>
      <PageHeader
        title="Cumplimiento legal"
        description="Las jornadas de todas las obras revisadas con el Código de Trabajo: feriados y descansos, topes de 12 h y semanales, rachas sin descanso y descanso entre jornadas."
        actions={
          <Button
            variant="outline"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['payroll', 'compliance'] })}
            disabled={report.isFetching}
          >
            <RefreshCwIcon className={cn(report.isFetching && 'animate-spin')} /> Actualizar
          </Button>
        }
      />

      <Stagger className="mb-4 grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        <SeverityTile
          severity="infraction"
          icon={OctagonAlertIcon}
          value={counts?.infraction}
          active={search.severity === 'infraction'}
          onClick={() => severityFilter('infraction')}
        />
        <SeverityTile
          severity="warning"
          icon={AlertTriangleIcon}
          value={counts?.warning}
          active={search.severity === 'warning'}
          onClick={() => severityFilter('warning')}
        />
        <SeverityTile
          severity="info"
          icon={InfoIcon}
          value={counts?.info}
          active={search.severity === 'info'}
          onClick={() => severityFilter('info')}
        />
        <StaggerItem>
          <Card className="relative h-full overflow-hidden p-4 sm:p-5">
            <div className="pointer-events-none absolute -top-16 -right-10 size-44 rounded-full bg-warning/15 blur-3xl" />
            <div className="relative">
              <div className="flex items-center justify-between gap-2 text-muted-foreground text-sm">
                Días revisados
                <span className="flex size-8 items-center justify-center rounded-lg bg-warning-soft text-warning-soft-foreground">
                  <ScaleIcon className="size-4" />
                </span>
              </div>
              {data ? (
                <div className="mt-2 font-semibold text-[26px] tracking-tight tabular">
                  <AnimatedNumber value={data.daysChecked} format={(n) => String(Math.round(n))} />
                </div>
              ) : (
                <Skeleton className="mt-3 h-8 w-20" />
              )}
              <div className="mt-1 hidden text-muted-foreground text-xs sm:block">
                {data
                  ? `${data.workers.length} trabajador${data.workers.length === 1 ? '' : 'es'} con jornadas en el rango`
                  : ' '}
              </div>
            </div>
          </Card>
        </StaggerItem>
      </Stagger>

      <DataTable
        columns={columns}
        data={data?.findings}
        loading={report.isFetching}
        getRowId={(f) => f.id}
        onRowClick={openDay}
        toolbar={
          <FilterBar>
            <FilterSelect
              label="Proyecto"
              width="md"
              value={search.project ?? 'all'}
              onValueChange={(v) => setSearch({ project: v === 'all' ? undefined : v, worker: undefined })}
              options={[
                { value: 'all', label: 'Todos los proyectos' },
                ...(projects.data ?? []).map((p) => ({
                  value: p.id,
                  label: p.code ? `${p.code} · ${p.name}` : p.name,
                })),
              ]}
            />
            <FilterSelect
              label="Trabajador"
              width="md"
              value={search.worker ?? 'all'}
              onValueChange={(v) => setSearch({ worker: v === 'all' ? undefined : v })}
              options={[
                { value: 'all', label: 'Todo el personal' },
                ...(data?.workers ?? []).map((w) => ({ value: w.id, label: w.name })),
                ...(search.worker && data && !data.workers.some((w) => w.id === search.worker)
                  ? [{ value: search.worker, label: 'Trabajador filtrado' }]
                  : []),
              ]}
            />
            <FilterSelect
              label="Nivel"
              width="md"
              value={search.severity ?? 'all'}
              onValueChange={(v) =>
                setSearch({ severity: v === 'all' ? undefined : (v as ComplianceSeverity) })
              }
              options={[
                { value: 'all', label: 'Todos los niveles' },
                ...(['infraction', 'warning', 'info'] as const).map((s) => ({
                  value: s,
                  label: SEVERITY[s].plural,
                })),
              ]}
            />
            <FilterSelect
              label="Tipo de hallazgo"
              width="md"
              value={search.rule ?? 'all'}
              onValueChange={(v) =>
                setSearch({ rule: v === 'all' ? undefined : (v as ComplianceFinding['rule']) })
              }
              options={[
                { value: 'all', label: 'Todos los tipos' },
                ...COMPLIANCE_RULES.map((r) => {
                  const n = data?.byRule.find((x) => x.rule === r.code)?.count
                  return { value: r.code, label: n ? `${r.name} (${n})` : r.name }
                }),
              ]}
            />
            <FilterDateRange
              value={{ from, to }}
              clearable={false}
              max={today}
              onChange={(r) => setSearch({ from: r.from, to: r.to ?? r.from })}
            />
            <ClearFilters
              active={filtered}
              onClear={() =>
                setSearch({
                  project: undefined,
                  worker: undefined,
                  severity: undefined,
                  rule: undefined,
                  from: undefined,
                  to: undefined,
                })
              }
            />
          </FilterBar>
        }
        empty={
          counts && counts.total > 0 ? (
            <EmptyState
              icon={CheckCircle2Icon}
              title="Sin hallazgos con esos filtros"
              description="Hay hallazgos de otro nivel o tipo en el rango: limpiá el filtro para verlos."
            />
          ) : (
            <div className="flex flex-col items-center px-6 py-14 text-center">
              <div className="relative mb-5">
                <div className="absolute inset-0 scale-150 rounded-full bg-success/15 blur-2xl" />
                <div className="relative flex size-12 items-center justify-center rounded-2xl border border-border bg-card shadow-elevation-2">
                  <ShieldCheckIcon className="size-5 text-success" />
                </div>
              </div>
              <h3 className="font-semibold text-[15px]">Todo en orden</h3>
              <p className="mt-1.5 max-w-md text-muted-foreground text-sm">
                {data?.daysChecked
                  ? `Se revisaron ${data.daysChecked} días-trabajador del rango y ninguno tiene hallazgos.`
                  : 'No hay jornadas registradas en el rango.'}
              </p>
              <ul className="mt-5 grid max-w-xl gap-x-6 gap-y-1.5 text-left text-muted-foreground text-xs sm:grid-cols-2">
                {CHECKS.map((c) => (
                  <li key={c} className="flex items-start gap-1.5">
                    <CheckCircle2Icon className="mt-0.5 size-3.5 shrink-0 text-success" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          )
        }
      />
      <p className="mt-3 flex items-start gap-1.5 text-muted-foreground text-xs leading-relaxed">
        <InfoIcon className="mt-0.5 size-3.5 shrink-0" />
        La jornada, la semana y las rachas se miden por trabajador sumando todas sus obras. Infracción: la
        norma ya se excedió. Advertencia: legal, pero al límite. Informativa: sin juicio (incluye la
        integridad de los datos).
      </p>
    </Page>
  )
}

function SeverityTile({
  severity,
  icon: Icon,
  value,
  active,
  onClick,
}: {
  severity: ComplianceSeverity
  icon: typeof OctagonAlertIcon
  value: number | undefined
  active: boolean
  onClick: () => void
}) {
  const meta = SEVERITY[severity]
  return (
    <StaggerItem>
      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        className={cn(
          'group h-full w-full rounded-xl text-left transition-[box-shadow,transform] duration-150 ease-smooth',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring active:scale-[0.99]',
        )}
      >
        <Card
          className={cn(
            'relative h-full overflow-hidden p-4 transition-colors duration-150 group-hover:bg-accent/40 sm:p-5',
            active && 'ring-2 ring-ring/60',
          )}
        >
          <div className="relative">
            <div className="flex items-center justify-between gap-2 text-muted-foreground text-sm">
              {meta.plural}
              <span className={cn('flex size-8 items-center justify-center rounded-lg', meta.soft)}>
                <Icon className="size-4" />
              </span>
            </div>
            {value === undefined ? (
              <Skeleton className="mt-3 h-8 w-16" />
            ) : (
              <div
                className={cn(
                  'mt-2 font-semibold text-[26px] tracking-tight tabular',
                  value > 0 ? meta.text : 'text-muted-foreground',
                )}
              >
                <AnimatedNumber value={value} format={(n) => String(Math.round(n))} />
              </div>
            )}
            <div className="mt-1 hidden text-muted-foreground text-xs sm:block">{meta.hint}</div>
          </div>
        </Card>
      </button>
    </StaggerItem>
  )
}
